import { DescriptionList } from '@circlefin/components'
import { useTravelRuleRequired } from '@features/users/hooks/travel-rule'
import { DELAYED_WITHDRAWALS_SETTINGS_POLL_INTERVAL } from '@shared/apollo/pollInterval'
import { useDelayedWithdrawalsSettingsQuery } from '@shared/graphql'
import useTranslation from 'next-translate/useTranslation'

import { AddressStatus } from '../../../components'

import type {
  BlockchainAddressStatus,
  DelayedWithdrawalInfo,
  Maybe,
} from '@shared/graphql'

const { Label, Description } = DescriptionList

export interface StatusProps {
  /**
   * Address status.
   */
  status: BlockchainAddressStatus
  /**
   * Delayed withdrawals info object.
   */
  delayedWithdrawalInfo?: Maybe<DelayedWithdrawalInfo>
  /**
   * Custom className.
   */
  className?: string
}

export const Status: React.FC<StatusProps> = ({
  status,
  delayedWithdrawalInfo,
}) => {
  const { t } = useTranslation('address-book/containers')
  const { travelRuleRequired } = useTravelRuleRequired()

  const { data } = useDelayedWithdrawalsSettingsQuery({
    pollInterval: DELAYED_WITHDRAWALS_SETTINGS_POLL_INTERVAL,
    fetchPolicy: 'cache-and-network',
  })

  // Only show status if 1) delayed withdrawals are enabled or 2) travel rule is required
  if (!data?.delayedWithdrawalsSettings.enabled && !travelRuleRequired) {
    return null
  }

  return (
    <>
      <Label className="w-72" data-testid="wallet-status">
        {t('wallet-information.status')}
      </Label>
      <Description className="flex flex-col">
        <AddressStatus status={status} />

        {delayedWithdrawalInfo?.enabled && (
          <div
            className="text-neutral-subtlest type-body-sm"
            data-testid="wallet-delayed-withdrawals-info"
          >
            {t(`wallet-information.delayed-withdrawals.text`, {
              count: delayedWithdrawalInfo.withdrawalEnabledAfterHours,
            })}
          </div>
        )}
      </Description>
    </>
  )
}
